import { CommonModule } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatDialog, MatDialogModule } from '@angular/material/dialog';
import { ToastrService } from 'ngx-toastr';
import { take } from 'rxjs/operators';
import { User } from '../model/user';
import { getStoredUser } from '../helpers/user-storage.util';
import { MiscService } from '../services/misc.service';
import { UserService } from '../services/user.service';
import { CommonUtilityServiceService } from '../services/common-utility-service.service';
import { DocumentService } from '../services/document.service';
import { PdfComponent } from '../pdf-viewer/pdf-viewer.component';
import { ZoneDocRequirementsComponent } from '../zone-documents/zone-doc-requirements/zone-doc-requirements.component';
import {
  ZoneDocUploadDialogComponent,
  ZoneDocUploadResult,
} from '../zone-documents/zone-doc-upload-dialog/zone-doc-upload-dialog.component';
import { ZoneDocEditDialogComponent } from '../zone-documents/zone-doc-edit-dialog/zone-doc-edit-dialog.component';
import { ZoneDocCoverage } from '../zone-doc-coverage-form/zone-doc-coverage-form.component';
import {
  licenseNodesUnderService,
  offeredZoneLicenses,
  zoneHasLicenseRows,
  zoneLicenseRequiredCount,
  zoneLicenseUnderReviewCount,
  LicenseRequirement,
} from '../zone-documents/zone-doc.util';
import { slugify } from '../shared/testid/testid.util';

/**
 * Documents — the vendor's profile-document + zone-document management surface,
 * lifted out of the legacy 4-tab manage-account mega-component into its own
 * routed page (CONNECT 2.0, Spec 6 · Task 2). The CON-140 functional design is
 * unchanged: Profile Documents is a simple upload/preview/download/delete card
 * list, and Zone Documents renders the shared Approach A (requirement-led)
 * <zone-doc-requirements> per zone, with the shared upload + edit/link dialogs.
 *
 * The page is self-sufficient — it fetches its own applicable license
 * requirements and the serviceTypeId -> `type` bridge rather than depending on
 * manage-account's in-memory state, so the two surfaces are fully decoupled.
 */
@Component({
  selector: 'app-documents',
  standalone: true,
  imports: [
    CommonModule,
    MatDialogModule,
    ZoneDocRequirementsComponent,
    ZoneDocUploadDialogComponent,
    ZoneDocEditDialogComponent,
  ],
  templateUrl: './documents.component.html',
  styleUrl: './documents.component.scss',
})
export class DocumentsComponent implements OnInit {
  user: User = {};

  // License requirements applicable to this PA (GET /license-requirements),
  // resolved server-side against their offered services. These — not the retired
  // vendorDocumentRequired boolean — are the source of the Zone Documents
  // requirement rows. `serviceTypeIdToType` bridges each association's
  // serviceTypeId (UUID) to the service `type` key the zone nodes use, built from
  // the service-type catalog.
  applicableLicenses: LicenseRequirement[] = [];
  serviceTypeIdToType: Record<string, string> = {};

  // Zone-document upload dialog context. The shared zone-doc-upload-dialog owns
  // the file/preview/coverage-form UI; this host owns only the per-node context
  // (which zone/service/sub-service the upload is scoped to) and the submit flag.
  // Blanket-sibling checkboxes let one upload cover more sibling sub-services.
  zoneDocUploadOpen: boolean = false;
  zoneDocUploadZone: any = null;
  zoneDocUploadService: any = null;
  zoneDocUploadSubType: any = null;
  zoneDocUploadSiblings: any[] = [];
  zoneDocUploadLicense: LicenseRequirement | null = null;
  // Whether the license requirement has a service-level node under the triggering
  // service; gates the upload dialog's sub-only requirement (see the dialog's
  // hasServiceLevelNode). Legacy uploads leave it true (gate N/A).
  zoneDocUploadHasServiceLevelNode: boolean = true;
  zoneDocUploadSubmitting: boolean = false;

  // Edit/link dialog context. The shared zone-doc-edit-dialog owns the coverage
  // form; this host owns the prefill context and the submit flag. On save it
  // PATCHes the document's coverage + expiration via document.service.
  editDocOpen: boolean = false;
  editDocZone: any = null;
  editDocDocument: any = null;
  editDocSelectableLicenses: LicenseRequirement[] = [];
  editDocInitialServiceType: string | null = null;
  editDocInitialSubTypes: string[] = [];
  editDocInitialExpiration: string = '';
  editDocInitialIssueDate: string = '';
  editDocInitialLicenseRequirementId: string | null = null;
  editDocSubmitting: boolean = false;

  constructor(
    private miscService: MiscService,
    private toastr: ToastrService,
    private userService: UserService,
    private utilityService: CommonUtilityServiceService,
    private documentService: DocumentService,
    private dialog: MatDialog,
  ) {}

  ngOnInit(): void {
    this.user = getStoredUser();

    // MiscService.fetch* / DocumentService.fetch* are ReplaySubject-backed and
    // never complete; take(1) ensures each subscription resolves once and tears
    // itself down (a forkJoin over them would hang).

    // Applicable license requirements for this PA — the source of the Zone
    // Documents requirement rows (replacing the vendorDocumentRequired boolean).
    this.documentService
      .fetchApplicableLicenseRequirements()
      .pipe(take(1))
      .subscribe((licenses: any[]) => {
        this.applicableLicenses = licenses ?? [];
      });

    // The requirement rows need the serviceTypeId -> `type` bridge (associations
    // key on serviceTypeId; zone nodes key on `type`).
    this.miscService
      .fetchServiceTypes()
      .pipe(take(1))
      .subscribe((result: any) => {
        if (result) {
          const idToType: Record<string, string> = {};
          for (const serviceType of result) {
            if (serviceType?.id && serviceType?.type) {
              idToType[serviceType.id] = serviceType.type;
            }
          }
          this.serviceTypeIdToType = idToType;
        }
      });
  }

  decodeState(state: string): string {
    return this.utilityService.decodeUsState(state);
  }

  // Stable, non-presentational testid part for a zone (state key or NATIONWIDE).
  zoneTestKey(serviceZone: any): string {
    return slugify(
      serviceZone?.nationwide ? 'nationwide' : serviceZone?.state ?? 'zone',
    );
  }

  // Zone Documents derivations live in the shared zone-doc.util so the vendor
  // Documents page (via <zone-doc-requirements>) and the internal console compute
  // them identically. These thin wrappers keep the host template working against
  // that single source of truth.

  // A zone has document rows to render when it has at least one applicable
  // license requirement (across its offered services / sub-services).
  zoneHasDocNodes(serviceZone: any): boolean {
    return zoneHasLicenseRows(
      serviceZone,
      this.applicableLicenses,
      this.serviceTypeIdToType,
    );
  }

  // Zone summary: count of license rows still outstanding — no valid or pending document.
  zoneRequiredCount(serviceZone: any): number {
    return zoneLicenseRequiredCount(
      serviceZone,
      this.applicableLicenses,
      this.serviceTypeIdToType,
    );
  }

  // Zone summary: how many license rows currently have a document under review.
  zoneUnderReviewCount(serviceZone: any): number {
    return zoneLicenseUnderReviewCount(
      serviceZone,
      this.applicableLicenses,
      this.serviceTypeIdToType,
    );
  }

  // Pluralized label for the zone summary's "required" half.
  zoneRequiredLabel(serviceZone: any): string {
    const count = this.zoneRequiredCount(serviceZone);
    return `${count} license${count === 1 ? '' : 's'} required`;
  }

  // Pluralized label for the zone summary's "under review" half.
  zoneUnderReviewLabel(serviceZone: any): string {
    const count = this.zoneUnderReviewCount(serviceZone);
    return `${count} license${count === 1 ? '' : 's'} under review`;
  }

  // The enabled specialties of a service — the sub-services the vendor turned on
  // for this zone. Used by the upload dialog's blanket-sibling list.
  enabledSubTypes(service: any): any[] {
    return (service?.serviceSubTypes ?? []).filter(
      (st: any) => st?.enabledForZone,
    );
  }

  // Opens the shared upload dialog pre-scoped to a single node. `subType` is null
  // when the node is the service itself. For a LICENSE upload (`license != null`)
  // the service is read-only, always-covered context (scoped sub-type is cleared)
  // and the "also apply to" checkboxes list ONLY the requirement's configured
  // sub-services under the triggering service, default checked. For a legacy
  // upload (`license == null`) it keeps the read-only scoped chip + the "also
  // apply to" checkboxes built from all enabled sub-services. The dialog owns the
  // file/preview state; this host owns only the per-node context.
  openZoneDocUpload(
    serviceZone: any,
    service: any,
    subType: any = null,
    license: LicenseRequirement | null = null,
  ) {
    this.zoneDocUploadZone = serviceZone;
    this.zoneDocUploadService = service;
    this.zoneDocUploadLicense = license;
    this.zoneDocUploadSubmitting = false;
    if (license) {
      // License upload: the service is the always-covered context (no scoped
      // sub-type), and the sibling checkboxes are the requirement's CONFIGURED
      // sub-services under this service — its sub nodes, default checked — not
      // all enabled sub-services.
      this.zoneDocUploadSubType = null;
      const nodesUnderService = licenseNodesUnderService(
        serviceZone,
        this.applicableLicenses,
        this.serviceTypeIdToType,
        license.id,
        service,
      );
      // A service-level node (subType null) makes a service-only upload valid; a
      // sub-only requirement forces >= 1 sub to be checked (see the dialog gate).
      this.zoneDocUploadHasServiceLevelNode = nodesUnderService.some(
        (node) => !node.subType,
      );
      this.zoneDocUploadSiblings = nodesUnderService
        .filter((node) => !!node.subType)
        .map((node) => ({
          type: node.subType.type,
          displayName: node.subType.displayName,
          checked: true,
        }));
    } else {
      // Legacy upload: keep the scoped node and offer the OTHER enabled
      // sub-services under the same service as blanket "also apply to" siblings.
      this.zoneDocUploadSubType = subType;
      // Legacy uploads have no requirement row to satisfy — gate N/A.
      this.zoneDocUploadHasServiceLevelNode = true;
      this.zoneDocUploadSiblings = this.enabledSubTypes(service)
        .filter((st: any) => !subType || st.type !== subType.type)
        .map((st: any) => ({
          type: st.type,
          displayName: st.displayName,
          checked: false,
        }));
    }
    this.zoneDocUploadOpen = true;
  }

  cancelZoneDocUpload() {
    this.zoneDocUploadOpen = false;
    this.zoneDocUploadZone = null;
    this.zoneDocUploadService = null;
    this.zoneDocUploadSubType = null;
    this.zoneDocUploadSiblings = [];
    this.zoneDocUploadLicense = null;
    this.zoneDocUploadHasServiceLevelNode = true;
    this.zoneDocUploadSubmitting = false;
  }

  // Saves the single-file upload emitted by the shared upload dialog. Coverage
  // carries the service `type` key, the sub-type `type` keys this upload blankets
  // (the scoped node plus any checked siblings), and the expiration date
  // converted to a Date.
  saveZoneDocUpload(payload: ZoneDocUploadResult) {
    if (!payload?.file) {
      this.toastr.error('Please select a file to upload.');
      return;
    }
    const emitted: ZoneDocCoverage | null = payload.coverage;
    const coverage = {
      serviceType: emitted?.serviceType ?? this.zoneDocUploadService?.type,
      serviceSubTypes: emitted?.serviceSubTypes ?? [],
      expirationDate: emitted?.expirationDate
        ? new Date(emitted.expirationDate)
        : null,
      // Tag the document with the row's license requirement + captured issue date
      // (connect-only). The coverage form echoes the license id from the dialog.
      licenseRequirementId:
        emitted?.licenseRequirementId ?? this.zoneDocUploadLicense?.id ?? null,
      issueDate: emitted?.issueDate ? new Date(emitted.issueDate) : null,
    };
    const serviceZone = this.zoneDocUploadZone;
    // Single file only — wrap it in a FileList-like array for the upload service.
    const files = [payload.file] as unknown as FileList;
    this.zoneDocUploadSubmitting = true;
    this.toastr.info('Uploading service zone documents...');
    this.documentService
      .uploadServiceZoneDocuments(this.user, serviceZone, files, coverage)
      .subscribe({
        next: (response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            this.zoneDocUploadSubmitting = false;
            this.toastr.error(
              apiResponse.message || 'An error occurred. Please try again.',
            );
          } else {
            const updatedUser = response.data;
            if (updatedUser) {
              this.user = { ...this.user, ...updatedUser };
              localStorage.setItem('user', JSON.stringify(this.user));
              this.toastr.success(apiResponse.message);
              this.cancelZoneDocUpload();
            } else {
              this.zoneDocUploadSubmitting = false;
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        },
        error: () => {
          this.zoneDocUploadSubmitting = false;
          this.toastr.error('An error occurred. Please try again.');
        },
      });
  }

  deleteZoneDocument(serviceZone: any, documentId: string) {
    if (this.user.id) {
      this.documentService
        .deleteZoneDocument(
          this.user.id,
          documentId,
          serviceZone.nationwide ? 'NATIONWIDE' : serviceZone.state,
        )
        .subscribe((response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            if (apiResponse.message) {
              this.toastr.error(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          } else {
            const updatedUser = response.data;
            if (updatedUser) {
              this.user = { ...this.user, ...updatedUser };
              localStorage.setItem('user', JSON.stringify(this.user));
              this.toastr.success(apiResponse.message);
            } else {
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        });
    }
  }

  downloadDocument(document: any) {
    this.toastr.info('Downloading document...');
    this.documentService.downloadDocument(document);
  }

  // Preview reuses the app's existing PDF viewer dialog. Non-PDF files have no
  // inline render, so the viewer simply shows what the browser can (it degrades
  // to the raw blob); the download action remains the reliable path for those.
  // The zone-doc requirement rows gate their own eye icon on the file being a
  // PDF before emitting (preview).
  openPdfViewer(document: any): void {
    this.documentService
      .downloadPdfToViewer(document)
      .then((result) => {
        this.dialog.open(PdfComponent, {
          height: '90%',
          width: '50%',
          panelClass: 'pdf-viewer-dialog-container',
          data: { pdfSrc: result.url, filename: result.filename },
        });
      })
      .catch(() => {
        this.toastr.error('Preview unavailable due to an error.');
      });
  }

  // Converts a stored expiration value (ISO string or Date) into the yyyy-MM-dd
  // string the coverage form's date input expects. Uses the UTC date portion so
  // it round-trips with the `new Date(...)` conversion the save call applies.
  private toDateInputValue(value: any): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    return Number.isNaN(date.getTime()) ? '' : date.toISOString().slice(0, 10);
  }

  // Opens the shared coverage + expiration edit dialog for an existing zone
  // document. Prefills the current coverage + expiration + linked requirement.
  // Coverage is derived in the dialog from the chosen requirement's node(s); a
  // legacy/Additional doc opens un-linked and can be moved under a named
  // requirement's row (or saved expiration-only when none apply).
  openEditDocDialog(serviceZone: any, zoneDocument: any): void {
    this.editDocZone = serviceZone;
    this.editDocDocument = zoneDocument;
    // The "applies to requirement" options are the distinct licenses that produce
    // a real row in this zone (every offered requirement derives >= 1 node), so a
    // legacy/Additional doc can be moved under a named requirement's row.
    this.editDocSelectableLicenses = offeredZoneLicenses(
      serviceZone,
      this.applicableLicenses,
      this.serviceTypeIdToType,
    );
    this.editDocInitialServiceType = zoneDocument?.serviceType ?? null;
    this.editDocInitialSubTypes = zoneDocument?.serviceSubTypes ?? [];
    this.editDocInitialExpiration = this.toDateInputValue(
      zoneDocument?.expirationDate,
    );
    this.editDocInitialIssueDate = this.toDateInputValue(
      zoneDocument?.issueDate,
    );
    this.editDocInitialLicenseRequirementId =
      zoneDocument?.licenseRequirementId ?? null;
    this.editDocSubmitting = false;
    this.editDocOpen = true;
  }

  cancelEditDoc(): void {
    this.editDocOpen = false;
    this.editDocZone = null;
    this.editDocDocument = null;
    this.editDocSelectableLicenses = [];
    this.editDocInitialServiceType = null;
    this.editDocInitialSubTypes = [];
    this.editDocInitialExpiration = '';
    this.editDocInitialIssueDate = '';
    this.editDocInitialLicenseRequirementId = null;
    this.editDocSubmitting = false;
  }

  // Saves the coverage edit emitted by the shared edit dialog. Sends the service
  // `type` key, the sub-type `type` keys this document blankets, and the
  // expiration date converted to a Date, to the PATCH /document/zone/{documentId}
  // endpoint. `serviceZoneIdentifier` is derived the same way as the
  // delete-zone-document call (NATIONWIDE or state). On success the returned user
  // is spliced into this.user; on error the dialog stays open.
  saveEditDoc(emitted: ZoneDocCoverage | null): void {
    if (!emitted) {
      this.toastr.error('An error occurred. Please try again.');
      return;
    }
    if (!this.user.id || !this.editDocDocument?.id) {
      this.toastr.error('An error occurred. Please try again.');
      return;
    }
    const serviceZone = this.editDocZone;
    const serviceZoneIdentifier = serviceZone?.nationwide
      ? 'NATIONWIDE'
      : serviceZone?.state;
    const derivedServiceType = emitted.serviceType ?? null;
    // Never persist a dangling link: a null serviceType can match no license row
    // (row matching keys on serviceType + licenseRequirementId), so a link with no
    // derived coverage would strand the document off every row unrecoverably. Drop
    // the licenseRequirementId whenever no service is derived.
    const linkedRequirementId = derivedServiceType
      ? emitted.licenseRequirementId ?? null
      : null;
    const body = {
      userId: this.user.id,
      serviceZoneIdentifier,
      // A null serviceType keeps the document an Additional document (no
      // requirement chosen / none apply) — the backend tolerates it. A chosen
      // requirement derives a non-null serviceType so the doc lands under its row.
      serviceType: derivedServiceType,
      serviceSubTypes: emitted.serviceSubTypes ?? [],
      expirationDate: emitted.expirationDate
        ? new Date(emitted.expirationDate)
        : null,
      // Coverage edit is a FULL replacement server-side, so the current/chosen
      // licenseRequirementId must be sent to preserve (or set) the link; the
      // coverage form echoes the dialog's selected requirement. issueDate is
      // connect-only and does not itself reset review.
      licenseRequirementId: linkedRequirementId,
      issueDate: emitted.issueDate ? new Date(emitted.issueDate) : null,
    };
    this.editDocSubmitting = true;
    this.documentService
      .updateZoneDocumentCoverage(this.user.id, this.editDocDocument.id, body)
      .subscribe({
        next: (response: any) => {
          const apiResponse = response;
          if (apiResponse.status === 'FAILURE') {
            this.editDocSubmitting = false;
            this.toastr.error(
              apiResponse.message || 'An error occurred. Please try again.',
            );
          } else {
            const updatedUser = response.data;
            if (updatedUser) {
              this.user = { ...this.user, ...updatedUser };
              localStorage.setItem('user', JSON.stringify(this.user));
              this.toastr.success(apiResponse.message);
              this.cancelEditDoc();
            } else {
              this.editDocSubmitting = false;
              this.toastr.error('An error occurred. Please try again.');
            }
          }
        },
        error: () => {
          this.editDocSubmitting = false;
          this.toastr.error('An error occurred. Please try again.');
        },
      });
  }
}
